/**
 * Workbench Kernel v2 — unified resolveActions + capability preview pipeline.
 *
 * W1 rule-table (judge which actions exist) → CR4/CR5 capability overlay (editor)
 * → resolveActionsFromDecisions. W8: capability-forbidden actions stay reachable in More/command.
 *
 * WB3-L9 — consumes permissionKeys for capability preview; kernel does not grant permissions.
 *
 * @see doc/system-completion-plan/01-target/CAPABILITY_RESOLVER_SSOT.md §6
 * @see .cursor/plans/archived/2026-q2/workbench-kernel-v2/stream-k1-facade.md
 */

import type { UniversalBaseEntity } from '@shared/types/entity';
import { allowDecision, type CapabilityDecision } from '@shared/capability';
import { filterCollectionContactFlowWorkbenchActions } from './collection-contact-flow-action-eligibility';
import { filterCollectionCommitmentWorkbenchActions } from './collection-commitment-action-eligibility';
import { resolveActions, getVisibleActions } from './action-resolver';
import type {
  ResolveActionsInput,
  WorkbenchAction,
  WorkbenchActionPlacement,
} from './action-types';
import {
  buildCapabilityPreviewContexts,
  resolveActionsFromDecisions,
  resolveCapabilityPreviewLocal,
  type CapabilityPreviewActionSource,
  type CapabilityPreviewInput,
  type CapabilityPreviewScope,
  type WorkbenchActionCapabilityState,
} from './capability-preview';
import type { WorkbenchFocus } from './workbench-focus';
import { resolveDefaultIntent } from './workbench-focus';
import { resolveIntentActionOrdering, type WorkbenchIntent } from './workbench-intent';
import type { PathPlane } from '@shared/tenant-host/pathPlanes';
import type { UserRole } from '@shared/types/roles';
import type { WorkbenchRole } from './workbench-role-cockpit-config';
import { filterWorkbenchActionsForRole } from './workbench-role-action-visibility';

export const WORKBENCH_CAPABILITY_ACTIONS = ['read', 'create', 'update', 'delete'] as const;

export type WorkbenchCapabilityPreviewAction = (typeof WORKBENCH_CAPABILITY_ACTIONS)[number];

export function isWorkbenchCapabilityPreviewAction(
  action: string
): action is WorkbenchCapabilityPreviewAction {
  return (WORKBENCH_CAPABILITY_ACTIONS as readonly string[]).includes(action);
}

export type WorkbenchResolvedAction = WorkbenchAction & WorkbenchActionCapabilityState;

export interface ResolveWorkbenchActionsOptions {
  decisions?: readonly CapabilityDecision[];
  previewInputs?: readonly CapabilityPreviewInput[];
  permissionKeys?: readonly string[];
  capabilityScope?: Partial<CapabilityPreviewScope>;
  resolvePreviewDecision?: (input: CapabilityPreviewInput) => CapabilityDecision | undefined;
  /** Role cockpit — hide workflow starters not relevant to the active workbench role. */
  workbenchRole?: WorkbenchRole;
}

export interface ResolveFocusIntentInput {
  role: UserRole;
  status: string;
  plane: PathPlane;
  permissionKeys?: readonly string[];
  focus?: WorkbenchFocus;
}

/** Resolves effective workbench intent from explicit focus or role/domain defaults. */
export function resolveFocusIntent(input: ResolveFocusIntentInput): WorkbenchIntent {
  if (input.focus?.intent !== undefined) {
    return input.focus.intent;
  }

  const rootDomain = input.focus?.root.businessDomain ?? 'entity';
  return resolveDefaultIntent(input.role, rootDomain, input.permissionKeys, input.focus?.variant);
}

/**
 * Demotes capability-forbidden actions from primary/secondary into More (W8 — no silent hide).
 */
export function ensureCapabilityForbiddenReachable<T extends WorkbenchAction>(
  actions: readonly T[]
): T[] {
  const visibleIds = new Set(getVisibleActions(actions).map((action) => action.id));

  return actions.map((action) => {
    if (!action.forbidden || !visibleIds.has(action.id) || action.placement === 'command-only') {
      return action;
    }
    return { ...action, placement: 'more' as WorkbenchActionPlacement };
  });
}

function resolveCapabilityDecisions(
  previewInputs: readonly CapabilityPreviewInput[],
  options: ResolveWorkbenchActionsOptions
): CapabilityDecision[] {
  if (options.decisions !== undefined && options.decisions.length > 0) {
    return [...options.decisions];
  }

  if (options.resolvePreviewDecision) {
    const keys = options.permissionKeys ?? [];
    const resolvePreviewDecision = options.resolvePreviewDecision;
    return previewInputs.map((input) => {
      const resolved = resolvePreviewDecision(input);
      if (resolved) {
        return resolved;
      }
      const [local] = resolveCapabilityPreviewLocal([input], keys);
      return local ?? allowDecision('domain');
    });
  }

  return resolveCapabilityPreviewLocal(previewInputs, options.permissionKeys ?? []);
}

function buildPreviewSources(
  actions: readonly WorkbenchAction[],
  scope: CapabilityPreviewScope
): CapabilityPreviewActionSource[] {
  return actions.map((workbenchAction) => ({
    workbenchAction,
    scope,
  }));
}

/**
 * Unified Workbench action pipeline: resolveActions → capability preview → merge decisions.
 */
/** SSOT lookup — palette · inspector · AI approve must resolve handlerRef by action id. */
export function resolveWorkbenchActionById(
  actionId: string,
  actions: readonly WorkbenchResolvedAction[]
): WorkbenchResolvedAction | undefined {
  return actions.find((action) => action.id === actionId);
}

function applyRoleVisibilityFilter<T extends WorkbenchAction>(
  actions: readonly T[],
  workbenchRole?: WorkbenchRole
): T[] {
  if (!workbenchRole) {
    return [...actions];
  }
  return filterWorkbenchActionsForRole(actions, workbenchRole);
}

export function resolveWorkbenchActions(
  object: UniversalBaseEntity,
  input: ResolveActionsInput,
  options: ResolveWorkbenchActionsOptions = {}
): WorkbenchResolvedAction[] {
  const capabilityInput: ResolveActionsInput = {
    ...input,
    skipLegacyPermissionFilter: true,
  };
  const baseActions = filterCollectionCommitmentWorkbenchActions(
    filterCollectionContactFlowWorkbenchActions(resolveActions(object, capabilityInput), object),
    object
  );

  const domain =
    options.capabilityScope?.domain ?? input.businessDomain ?? object.businessDomain ?? 'entity';

  const scope: CapabilityPreviewScope = {
    domain,
    objectId: options.capabilityScope?.objectId ?? object.id,
    ...options.capabilityScope,
  };

  const previewInputs =
    options.previewInputs ??
    buildCapabilityPreviewContexts(buildPreviewSources(baseActions, scope));

  if (previewInputs.length === 0) {
    return applyRoleVisibilityFilter(
      applyIntentOrderingToActions(baseActions, input, options),
      options.workbenchRole
    );
  }

  const decisions = resolveCapabilityDecisions(previewInputs, options);
  const merged = resolveActionsFromDecisions(baseActions, decisions, previewInputs);

  return applyRoleVisibilityFilter(
    applyIntentOrderingToActions(ensureCapabilityForbiddenReachable(merged), input, options),
    options.workbenchRole
  );
}

function applyIntentOrderingToActions<T extends WorkbenchAction>(
  actions: readonly T[],
  input: ResolveActionsInput,
  options: ResolveWorkbenchActionsOptions
): T[] {
  const intent = resolveFocusIntent({
    role: input.role,
    status: input.status,
    plane: input.plane,
    ...((options.permissionKeys ?? input.permissionKeys)
      ? { permissionKeys: options.permissionKeys ?? input.permissionKeys }
      : {}),
    ...(input.focus ? { focus: input.focus } : {}),
  });
  return resolveIntentActionOrdering(actions, intent, input.role);
}
